import { usePatientImmunizations } from '@ctw/shared/api/fhir/immunizations';
import type { ImmunizationModel } from '@ctw/shared/api/fhir/models/immunization';
import { CopyToClipboard } from '@ctw/shared/components/copy-to-clipboard';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { useMainContentHeader } from '@ctw/shared/components/layout/main-content-with-dockable-drawer';
import type { LegacyTableColumn } from '@ctw/shared/components/tables/legacy-table';
import { EmptyPatientTable } from '@ctw/shared/content/empty-patients-table';
import { ResourceTable } from '@ctw/shared/content/resource/resource-table';
import type { ResourceTableSortOption } from '@ctw/shared/content/resource/resource-table-actions';
import {
  RenderSyncedWithRecordIcon,
  ResourceTitleColumn,
} from '@ctw/shared/content/resource/resource-title-column';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useFilteredSortedData } from '@ctw/shared/hooks/use-filtered-sorted-data';
import { ResourceTableActions } from '../resource/resource-table-actions';

export const PatientImmunizations = withErrorBoundary({
  boundaryName: 'PatientImmunizations',
  Component: () => {
    const { requestContext } = useCTW();
    const { openResourceDrawer } = useResourceDrawer({ enableReadActions: true });
    const patientImmunizationsQuery = usePatientImmunizations();

    const { data, setSort, defaultSort } = useFilteredSortedData({
      cacheKey: 'patient-immunizations',
      defaultSort: defaultImmunizationSort,
      records: patientImmunizationsQuery.data,
    });
    const isEmptyQuery = patientImmunizationsQuery.data?.length === 0;
    const hasZeroFilteredRecords = !isEmptyQuery && data.length === 0;

    useMainContentHeader(
      'PatientImmunizationsContentHeader',
      () => (
        <ResourceTableActions
          sortOptions={{
            defaultSort,
            options: immunizationSortOptions,
            onChange: setSort,
          }}
        />
      ),
      [defaultSort, immunizationSortOptions],
    );

    return (
      <div>
        <ResourceTable
          title="Immunizations"
          titleVisuallyHidden={true}
          showTableHead={true}
          isLoading={patientImmunizationsQuery.isLoading}
          data={data}
          columns={patientImmunizationsColumns(requestContext.builderId)}
          onRowClick={({ telemetry, record }) => openResourceDrawer({ telemetry, model: record })}
          enableReadActions={true}
          emptyMessage={
            <EmptyPatientTable
              hasZeroFilteredRecords={hasZeroFilteredRecords}
              resourceName="immunizations"
            />
          }
        />
      </div>
    );
  },
});

const defaultImmunizationSort: ResourceTableSortOption<ImmunizationModel> = {
  display: 'Name (A-Z)',
  sorts: [
    { key: 'description', dir: 'asc' },
    { key: 'occurrence', dir: 'desc', isDate: true },
  ],
};

const immunizationSortOptions: Array<ResourceTableSortOption<ImmunizationModel>> = [
  defaultImmunizationSort,
  {
    display: 'Name (Z-A)',
    sorts: [
      { key: 'description', dir: 'desc' },
      { key: 'occurrence', dir: 'desc', isDate: true },
    ],
  },
  {
    display: 'Date (New to Old)',
    sorts: [{ key: 'occurrence', dir: 'desc', isDate: true }],
  },
  {
    display: 'Date (Old to New)',
    sorts: [{ key: 'occurrence', dir: 'asc', isDate: true }],
  },
];

const patientImmunizationsColumns = (builderId: string) => {
  const immunizationColumns: Array<LegacyTableColumn<ImmunizationModel>> = [
    {
      title: 'Immunization',
      widthPercent: 60,
      minWidth: 320,
      render: (immunization) => (
        <ResourceTitleColumn
          title={<CopyToClipboard>{immunization.description}</CopyToClipboard>}
          renderIcon={RenderSyncedWithRecordIcon(immunization.ownedByBuilder(builderId))}
        />
      ),
    },
    {
      title: 'Date Given',
      dataIndex: 'occurrence',
    },
  ];

  return immunizationColumns;
};
